import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import "../../design/components/text-field.css";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { tabKey, type TabContext } from "../../session/tab-context";
import { withoutControlCharacters } from "../lead-display";
import { leadKeys, useLeadView, type CardLead } from "../lead-queries";
import { useArchiveLead } from "./archive-queries";
import "./archive.css";

// «Archivia» nella testata della scheda, accanto all'owner (piano M3 Task 12 e decisione P36, mock-up `scheda-lead.html` e
// l'«Archivia» di `initLeadCard`):
// tre stati, il pulsante leggero, la domanda in riga con il motivo facoltativo e «Sì, archivia» / «No», e il fatto,
// che porta all'elenco con l'avviso (lo fa la scheda, `onArchived`). Lo vede chi ha `permissions.canArchive` (la
// funzione `trash_restore` e la modifica del Lead): lo decide il server. Un rifiuto si dice in riga e la domanda resta;
// un rifiuto della scheda intera (versione, permessi, Lead già archiviato o sparito) la fa anche rileggere.

/** Il massimo del motivo (`archiveLeadBodySchema`): oltre, il server risponderebbe 422. */
const REASON_MAX_LENGTH = 500;

/** I rifiuti dopo cui la scheda va riletta: un nuovo «Sì, archivia» parte dalla versione nuova, o la scheda dice che non c'è. */
const CARD_REFUSALS: ReadonlySet<string> = new Set(["version_conflict", "lead_archived", "lead_not_found", "permission_denied"]);

type Props = {
  tab: TabContext;
  lead: CardLead;
  /** Il Lead è archiviato: la scheda torna all'elenco con l'avviso. */
  onArchived: () => void;
};

export function ArchiveButton({ tab, lead, onArchived }: Props) {
  const t = useT();
  const view = useLeadView();
  const queryClient = useQueryClient();
  const archive = useArchiveLead(tab, lead.id);
  const askId = useId();
  const reasonId = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const focusButton = useRef(false);

  // La domanda si apre sul motivo; «No» (o Esc) riporta il focus su «Archivia».
  useEffect(() => {
    if (asking) reasonRef.current?.focus();
    else if (focusButton.current) {
      focusButton.current = false;
      buttonRef.current?.focus();
    }
  }, [asking]);

  function cancel() {
    if (archive.isPending) return;
    focusButton.current = true;
    setAsking(false);
    setReason("");
    setError(null);
  }

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (archive.isPending) return;
    setError(null);
    const written = withoutControlCharacters(reason).trim();
    try {
      await archive.mutateAsync({ version: lead.version, ...(written ? { reason: written } : {}) });
      onArchived();
    } catch (failure) {
      setError(failure);
      if (failure instanceof ApiRequestError && CARD_REFUSALS.has(failure.code)) {
        void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabKey(tab), lead.id, view), exact: true });
      }
    }
  }

  // Esc vale «No», dal motivo come dai pulsanti.
  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    cancel();
  }

  if (!asking) {
    return (
      <Button ref={buttonRef} variant="ghost" onClick={() => setAsking(true)}>
        <Icon name="trash" />
        {t("leadArchive.archive")}
      </Button>
    );
  }

  const message =
    error === null ? null : error instanceof ApiRequestError && error.code === "version_conflict" ? t("leadCard.conflict") : messageForError(t, error);
  return (
    <form className="g-archive-ask" aria-labelledby={askId} aria-busy={archive.isPending} onSubmit={(event) => void confirm(event)}>
      <p id={askId} className="g-archive-ask__question">
        {t("leadArchive.ask")}
      </p>
      <div className="g-archive-ask__reason">
        <label className="g-field__label" htmlFor={reasonId}>{t("leadArchive.reason")}</label>
        <input
          ref={reasonRef}
          id={reasonId}
          className="g-field__input"
          type="text"
          autoComplete="off"
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="g-archive-ask__buttons">
        <Button variant="primary" type="submit" disabled={archive.isPending} aria-busy={archive.isPending ? "true" : undefined} onKeyDown={handleKeyDown}>
          {t("leadArchive.yes")}
        </Button>
        <Button onClick={cancel} disabled={archive.isPending} onKeyDown={handleKeyDown}>
          {t("leadArchive.no")}
        </Button>
      </div>
      {message ? (
        <p className="g-field__error" role="alert">
          {message}
        </p>
      ) : null}
    </form>
  );
}
